import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, WritableSignal} from '@angular/core';
import {SearchContainer} from "../../core/definitions/search-container";
import {
  FilterMenuCategorySelectorComponent,
  CategoryMenuItem
} from "./filter-menu-category-selector/filter-menu-category-selector.component";
import {
  FilterMenuStoredSearchSelectorComponent
} from "./filter-menu-saved-search-selector/filter-menu-stored-search-selector.component";
import {
  FilterMenuFilterListComponent
} from "./filter-menu-filter-list/filter-menu-filter-list.component";
import {
  FilterMenuSearchSuggestionListComponent
} from "./filter-menu-search-suggestion-list/filter-menu-search-suggestion-list.component";
import {SearchExecutorService} from "../search-executor.service";
import {SearchFilterService} from "../search-filter.service";
import {SearchViewCategoryMenuService} from "../search-view-category-menu.service";
import {SearchFacetService} from "../search-facet.service";
import {SearchViewMenu} from "../../core/definitions/search-objects";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {StoredSearchServiceImplementation, StoredSearchService} from "../stored-search.service";
import {
  FloatingFeatureToggleSwitchComponent
} from "../../floating-feature-toggle-switch/floating-feature-toggle-switch.component";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {FeatureFlagsService} from "../../core/feature-flags.service";

@Component({
    selector: 'app-search-filter-menu-v2',
    imports: [
        FilterMenuCategorySelectorComponent,
        FilterMenuStoredSearchSelectorComponent,
        FilterMenuFilterListComponent,
        FilterMenuSearchSuggestionListComponent,
        FloatingFeatureToggleSwitchComponent,
        TranslateModule,
        MatIconButton,
        MatIcon,
    ],
    templateUrl: './search-filter-menu-v2.component.html',
    styleUrl: './search-filter-menu-v2.component.scss'
})
export class SearchFilterMenuV2Component implements OnInit, OnDestroy {
  // Injections
  private readonly categoryMenuService: SearchViewCategoryMenuService = inject(SearchViewCategoryMenuService);
  private readonly featureFlagService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly searchExecutorService: SearchExecutorService = inject(SearchExecutorService);
  private readonly searchFacetService: SearchFacetService = inject(SearchFacetService);
  private readonly searchFocusService: StoredSearchService = inject(StoredSearchService);
  private readonly searchFilterService: SearchFilterService = inject(SearchFilterService);
  private readonly translation: TranslateService = inject(TranslateService);

  @Input() searchContainer: SearchContainer;

  @Output() closeDrawer = new EventEmitter();

  categories: WritableSignal<CategoryMenuItem[]> = signal([]);
  subCategories: WritableSignal<CategoryMenuItem[]> = signal([]);
  fsi: StoredSearchServiceImplementation;
  loadingState: boolean = true;
  updateTrigger: WritableSignal<boolean> = signal(false);

  ngOnInit() {
    this.fsi = this.searchFocusService.createFSI(this.searchContainer);
    this.searchExecutorService.subscribeToSearchResult(this.searchContainer, this.initialize);
  }

  ngOnDestroy() {
    this.searchExecutorService.unSubscribeToSearchResult(this.searchContainer, this.initialize);
  }

  reloadPage() {
    window.location.reload();
  }

  triggerLoadingState() {
    this.loadingState = true;
  }

  private initialize = () => {
    if (this.searchContainer) {
      this.setAvailableCategories();

      if (this.searchContainer.searchResult) {
        this.setCheckedFilters().then();
        this.setFilterCount();
      }

      if (this.searchContainer.currentPathView.search_view.menus !== 'all_objects_home') {
        this.setAvailableSubCategories();
      }
      else {
        this.subCategories.set([]);
      }
    }

    this.updateTrigger.update(current => !current);

    if (this.loadingState) {
      this.loadingState = false;
    }
  }

  private searchViewMenuToCategoryMenu(menuItem: SearchViewMenu): CategoryMenuItem {
    let count: number;
    if (menuItem.menus && menuItem.menus.length > 0) {
      if (menuItem.facet_values) {
        count = this.searchFacetService.getMenuCount(menuItem, this.searchContainer);
      } else {
        count = 0;
        console.warn(`Menu item ${menuItem.title} has submenus but no facet values. Count set to 0.`);
      }
    } else {
      count = this.searchFacetService.getMenuCount(menuItem, this.searchContainer);
    }
    let item: CategoryMenuItem = {
      //count: this.searchFacetService.getMenuCount(menuItem, this.searchContainer),
      count: count,
      disabled: false,
      label: signal<string>(this.translation.instant(menuItem.title)),
      path: menuItem.path,
      type: 'child',
      menu: menuItem,
      icon: menuItem.icon
    }

    if (menuItem.menus?.length > 0) {
      item.children = [];
      item.path = '';
      item.type = 'header';

      item.children.push({
        disabled: false,
        label: signal<string>(`${this.translation.instant('TRANS__SEARCH_MENU__CATEGORY_ALL')} ${this.translation.instant(menuItem.title).toLowerCase()}`),
        path: menuItem.path,
        type: 'child',
        //count: this.searchFacetService.getMenuCount(menuItem, this.searchContainer),
        count: item.count,
        icon: menuItem.icon
      });

      for (const subMenu of menuItem.menus) {
        item.children.push(this.searchViewMenuToCategoryMenu(subMenu));
      }
    }

    return item;
  }

  private async setCheckedFilters() {
    if (!this.searchContainer.filtersFacets.filterGroups) {
      await this.searchFilterService.setCheckFilterGroups(this.searchContainer);
    }
    this.searchContainer.filtersFacets.filterGroups = this.searchContainer.filtersFacets.filterGroups
      ? this.searchContainer.filtersFacets.filterGroups.sort((a, b) => this.translation.instant(a.title).localeCompare(this.translation.instant(b.title)))
      : undefined;
  }

  private setFilterCount() {
    for (const filterGroup of this.searchContainer.filtersFacets.filterGroups || []) {
      for (const filter of filterGroup.checkFilters || []) {
        filter.count = this.searchFilterService.getFilterCount(filter, this.searchContainer);
      }
    }
  }

  private setAvailableCategories() {
    this.categoryMenuService.getCategoryMenus(['advanced', 'extended'].includes(this.searchContainer.queryContainer?.selectedQueryMenu?.query_type) ? 'object_filter' : 'all_objects_home').then(tempSearchViewMenus => {
      let tempCategories = [];

      tempCategories.push({
        disabled: false,
        icon: 'blur_on',
        label: signal<string>(this.translation.instant('TRANS__SEARCH_MENU_ALL_CATEGORIES')),
        path: 'home',
        type: 'child'
      } as CategoryMenuItem);

      if (['extended', 'advanced'].includes(this.searchContainer.queryContainer?.selectedQueryMenu?.query_type)) {
        tempCategories.push({
          disabled: false,
          label: signal<string>(`${this.translation.instant('TRANS__SEARCH_MENU__CATEGORY_ALL')} ${this.translation.instant('TRANS__SEARCH_VIEWS__MENU__OBJECTS').toLowerCase()}`),
          path: 'home/artifacts',
          type: 'child',
          icon: 'category'
        });
      }

      for (const menu of tempSearchViewMenus) {
        tempCategories.push(this.searchViewMenuToCategoryMenu(menu));
      }

      this.categories.set(tempCategories);
    })

  }

  private setAvailableSubCategories() {
    this.categoryMenuService.getCategoryMenus(this.searchContainer.currentPathView.search_view.menus).then((subMenus) => {
      let tempCategories = [];
      for (const menu of subMenus) {
        tempCategories.push(this.searchViewMenuToCategoryMenu(menu));
      }

      this.subCategories.set(tempCategories);
    })
  }

  readonly useNewLayout = this.featureFlagService.getFeatureFlags()?.experimental?.useNewMainMenu;
}
